import {
  copiesOwned as collectionCopiesOwned,
  grantCards as collectionGrantCards,
  loadCollection,
  onMissionVictory,
  oneOfEachOwned,
  STARTER_OWNED_IDS,
} from "../../../shared/collection";
import { resolveVerseId } from "../../../shared/deck/catalog";
import {
  countsFromIds,
  ownedCount,
  totalCount,
  type CardCounts,
} from "./counts";
import type {
  CampaignDifficulty,
  CampaignSave,
  MissionClearFlags,
  RewardOutcome,
} from "./types";
import { CAMPAIGN_MISSIONS, missionById } from "./missions";
import { campaignForMissionId } from "./registry";
import { CAMPAIGN_CHAPTER } from "./story";
import { GIDEON_CHAPTER } from "./gideon/story";

const STORAGE_KEY = "sots-campaign-proclaim-v1";
const NEH_ID = CAMPAIGN_CHAPTER.id;
const GID_ID = GIDEON_CHAPTER.id;

function ownedFromCollection(): CardCounts {
  return { ...loadCollection().ownedCounts };
}

function defaultSave(): CampaignSave {
  const ownedCounts = ownedFromCollection();
  return {
    version: 3,
    ownedCounts,
    deckCounts: oneOfEachOwned(),
    unlockedMissionIndex: 0,
    unlockedByCampaign: { [NEH_ID]: 0, [GID_ID]: 0 },
    missions: {},
  };
}

function sanitizeCounts(raw: unknown): CardCounts {
  if (!raw || typeof raw !== "object") return {};
  const out: CardCounts = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = typeof v === "number" ? Math.floor(v) : 0;
    if (n <= 0 || typeof k !== "string" || !k.length) continue;
    const id = resolveVerseId(k);
    out[id] = Math.max(out[id] ?? 0, n);
  }
  return out;
}

function clampDeckToOwned(
  deckCounts: CardCounts,
  ownedCounts: CardCounts,
): CardCounts {
  const deck: CardCounts = {};
  for (const [id, n] of Object.entries(deckCounts)) {
    const max = ownedCount(ownedCounts, id);
    if (max <= 0) continue;
    deck[id] = Math.min(Math.floor(n), max);
  }
  if (totalCount(deck) === 0) {
    return countsFromIds(STARTER_OWNED_IDS);
  }
  return deck;
}

function sanitize(raw: unknown): CampaignSave {
  const base = defaultSave();
  if (!raw || typeof raw !== "object") return base;
  const o = raw as Partial<CampaignSave>;

  // Collection is source of truth for owned cards
  const ownedCounts = ownedFromCollection();

  let deckCounts: CardCounts;
  if (o.deckCounts && typeof o.deckCounts === "object") {
    deckCounts = clampDeckToOwned(sanitizeCounts(o.deckCounts), ownedCounts);
  } else {
    deckCounts = oneOfEachOwned();
  }

  const unlocked =
    typeof o.unlockedMissionIndex === "number" && o.unlockedMissionIndex >= 0
      ? Math.min(
          Math.floor(o.unlockedMissionIndex),
          CAMPAIGN_MISSIONS.length - 1,
        )
      : 0;
  const unlockedByCampaign: Record<string, number> = {
    [NEH_ID]: unlocked,
    [GID_ID]: 0,
  };
  if (o.unlockedByCampaign && typeof o.unlockedByCampaign === "object") {
    for (const [cid, n] of Object.entries(o.unlockedByCampaign)) {
      if (typeof n === "number" && n >= 0) {
        unlockedByCampaign[cid] = Math.floor(n);
      }
    }
  }
  // Keep legacy field in sync with Nehemiah
  unlockedByCampaign[NEH_ID] = Math.max(
    unlockedByCampaign[NEH_ID] ?? 0,
    unlocked,
  );

  const missions: Record<string, MissionClearFlags> = {};
  if (o.missions && typeof o.missions === "object") {
    for (const [k, v] of Object.entries(o.missions)) {
      if (!v || typeof v !== "object") continue;
      const flags: MissionClearFlags = {};
      if (v.easy) flags.easy = true;
      if (v.standard) flags.standard = true;
      if (v.hard) flags.hard = true;
      missions[k] = flags;
    }
  }
  return {
    version: 3,
    ownedCounts,
    deckCounts,
    unlockedMissionIndex: unlockedByCampaign[NEH_ID] ?? 0,
    unlockedByCampaign,
    missions,
  };
}

export function loadCampaign(): CampaignSave {
  // Ensure collection exists / migrated before reading campaign
  loadCollection();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSave();
    return sanitize(JSON.parse(raw));
  } catch {
    return defaultSave();
  }
}

export function saveCampaign(save: CampaignSave): void {
  // Do not persist ownedCounts as authority — re-read from collection on load.
  // Still write a snapshot so older tools / migration paths see something sensible.
  const ownedCounts = ownedFromCollection();
  const payload: CampaignSave = {
    ...save,
    version: 2,
    ownedCounts,
    deckCounts: clampDeckToOwned(save.deckCounts, ownedCounts),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

/**
 * Reset mission progress only. Collection (cards + gallery) is kept.
 */
export function resetCampaign(): CampaignSave {
  const fresh = defaultSave();
  saveCampaign(fresh);
  return fresh;
}

export function isOwned(save: CampaignSave, verseId: string): boolean {
  return ownedCount(save.ownedCounts, verseId) > 0;
}

export function copiesOwned(save: CampaignSave, verseId: string): number {
  return ownedCount(save.ownedCounts, verseId);
}

/**
 * @param campaignId Defaults to Nehemiah for legacy callers.
 */
export function isMissionUnlocked(
  save: CampaignSave,
  index: number,
  campaignId: string = NEH_ID,
): boolean {
  const map = save.unlockedByCampaign;
  const unlocked =
    map && typeof map[campaignId] === "number"
      ? map[campaignId]!
      : campaignId === NEH_ID
        ? save.unlockedMissionIndex
        : 0;
  return index <= unlocked;
}

export function isDifficultyCleared(
  save: CampaignSave,
  missionId: string,
  difficulty: CampaignDifficulty,
): boolean {
  return !!save.missions[missionId]?.[difficulty];
}

export function unownedRewards(
  save: CampaignSave,
  missionId: string,
): string[] {
  const m = missionById(missionId);
  if (!m) return [];
  const zero = m.rewardCards.filter(
    (id) => ownedCount(save.ownedCounts, id) === 0,
  );
  if (zero.length) return [...zero];
  return [...m.rewardCards];
}

function grantCards(save: CampaignSave, ids: string[]): string[] {
  const granted = collectionGrantCards(ids);
  save.ownedCounts = ownedFromCollection();
  return granted;
}

function unlockNext(save: CampaignSave, missionId: string): void {
  const mission = missionById(missionId);
  if (!mission) return;
  const camp = campaignForMissionId(missionId);
  const ladder = camp?.missions ?? CAMPAIGN_MISSIONS;
  const campId = camp?.id ?? NEH_ID;
  if (mission.index + 1 >= ladder.length) return;

  const nextIdx = mission.index + 1;
  const map = { ...(save.unlockedByCampaign ?? {}) };
  map[campId] = Math.max(map[campId] ?? 0, nextIdx);
  save.unlockedByCampaign = map;
  if (campId === NEH_ID) {
    save.unlockedMissionIndex = Math.max(save.unlockedMissionIndex, nextIdx);
  }
}

function markCleared(
  save: CampaignSave,
  missionId: string,
  difficulty: CampaignDifficulty,
): void {
  const flags: MissionClearFlags = { ...save.missions[missionId] };
  flags[difficulty] = true;
  save.missions[missionId] = flags;
}

/**
 * On victory: unlock next mission, signature + gallery on finale, reward UI.
 * Difficulty is marked cleared when rewards are finalized.
 */
export function processVictory(
  save: CampaignSave,
  missionId: string,
  difficulty: CampaignDifficulty,
): { save: CampaignSave; reward: RewardOutcome; alreadyCleared: boolean } {
  const mission = missionById(missionId);
  if (!mission) {
    return { save, reward: { kind: "none" }, alreadyCleared: false };
  }

  const alreadyCleared = !!save.missions[missionId]?.[difficulty];
  const next: CampaignSave = {
    ...save,
    ownedCounts: ownedFromCollection(),
    deckCounts: { ...save.deckCounts },
    missions: { ...save.missions },
  };
  unlockNext(next, missionId);

  const camp = campaignForMissionId(missionId);
  const isFinale = !!(
    camp?.finaleMissionId && missionId === camp.finaleMissionId
  );
  const sig = onMissionVictory({
    missionId,
    chapterId: camp?.id ?? CAMPAIGN_CHAPTER.id,
    isFinale,
    signatureVerseId: camp?.signatureVerseId,
    alreadyCleared,
  });
  next.ownedCounts = ownedFromCollection();

  if (alreadyCleared) {
    saveCampaign(next);
    return { save: next, reward: { kind: "none" }, alreadyCleared };
  }

  const available = unownedRewards(next, missionId);

  if (difficulty === "hard") {
    const granted = grantCards(next, [...mission.rewardCards]);
    // Surface signature grant in auto UI if it happened and wasn't in trio grant list
    const show = [
      ...sig.signatureGranted.filter((id) => !granted.includes(id)),
      ...granted,
    ];
    markCleared(next, missionId, difficulty);
    saveCampaign(next);
    return {
      save: next,
      reward: show.length ? { kind: "auto", granted: show } : { kind: "none" },
      alreadyCleared,
    };
  }

  // Easy / Standard: signature may auto-grant; still offer a pick from the trio
  if (sig.signatureGranted.length && available.length === 0) {
    markCleared(next, missionId, difficulty);
    saveCampaign(next);
    return {
      save: next,
      reward: { kind: "auto", granted: sig.signatureGranted },
      alreadyCleared,
    };
  }

  saveCampaign(next);
  return {
    save: next,
    reward: {
      kind: "pick",
      options: available,
      alsoGranted: sig.signatureGranted.length
        ? sig.signatureGranted
        : undefined,
    },
    alreadyCleared,
  };
}

/** Apply a single pick; marks the difficulty cleared. */
export function applyRewardPick(
  save: CampaignSave,
  verseId: string,
  allowed: readonly string[],
  missionId: string,
  difficulty: CampaignDifficulty,
): CampaignSave {
  const next: CampaignSave = {
    ...save,
    ownedCounts: ownedFromCollection(),
    deckCounts: { ...save.deckCounts },
    missions: { ...save.missions },
  };
  if (allowed.includes(verseId)) {
    grantCards(next, [verseId]);
  }
  markCleared(next, missionId, difficulty);
  saveCampaign(next);
  return next;
}

/** Skip pick or finish auto — mark difficulty cleared without new cards. */
export function finalizeVictory(
  save: CampaignSave,
  missionId: string,
  difficulty: CampaignDifficulty,
): CampaignSave {
  const next: CampaignSave = {
    ...save,
    ownedCounts: ownedFromCollection(),
    deckCounts: { ...save.deckCounts },
    missions: { ...save.missions },
  };
  markCleared(next, missionId, difficulty);
  saveCampaign(next);
  return next;
}

/** Live owned count from collection (ignores stale save snapshot). */
export function liveCopiesOwned(verseId: string): number {
  return collectionCopiesOwned(verseId);
}
